import { isAxiosError, type AxiosError } from 'axios'

/** RFC 7807 / ASP.NET ProblemDetails (+ validation `errors`). */
export type ProblemDetailsLike = {
  type?: string
  title?: string
  detail?: string
  status?: number
  errors?: Record<string, string[] | string>
  traceId?: string
}

function flattenValidationErrors(errors: ProblemDetailsLike['errors']): string | null {
  if (!errors || typeof errors !== 'object') return null
  const lines: string[] = []
  for (const [key, val] of Object.entries(errors)) {
    if (Array.isArray(val)) {
      for (const m of val) lines.push(`${key}: ${m}`)
    } else if (typeof val === 'string') {
      lines.push(`${key}: ${val}`)
    }
  }
  return lines.length > 0 ? lines.join('\n') : null
}

function parseProblemBody(data: unknown): ProblemDetailsLike | null {
  if (!data) return null
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    return data as ProblemDetailsLike
  }
  if (typeof data === 'string') {
    const t = data.trim()
    if (t.startsWith('{')) {
      try {
        return JSON.parse(t) as ProblemDetailsLike
      } catch {
        return null
      }
    }
  }
  return null
}

export type ApiErrorParts = {
  /** App context, e.g. "Save failed" */
  title?: string
  statusCode: number | null
  /** ProblemDetails `title` from API (e.g. "Bad Request") when distinct from the detail body */
  problemTitle?: string
  /** ProblemDetails `detail`, validation text, or fallback */
  detail: string
}

function getAxiosErrorParts(error: AxiosError<unknown>, fallback: string): Omit<ApiErrorParts, 'title'> {
  const status = error.response?.status
  const data = error.response?.data

  if (data instanceof Blob) {
    return { statusCode: status ?? null, detail: fallback }
  }

  if (!error.response) {
    const code = error.code
    const hint =
      code === 'ECONNABORTED'
        ? 'Request timed out'
        : code === 'ERR_NETWORK'
          ? 'Network error'
          : error.message || fallback
    return { statusCode: null, detail: hint }
  }

  const body = parseProblemBody(data)
  const validation = body ? flattenValidationErrors(body.errors) : null

  const detailText = body?.detail?.trim()
  const apiTitle = body?.title?.trim()

  let detail: string
  let problemTitle: string | undefined

  if (validation) {
    detail = validation
    problemTitle = apiTitle || undefined
  } else if (detailText) {
    detail = detailText
    if (apiTitle && apiTitle !== detailText) {
      problemTitle = apiTitle
    }
  } else if (apiTitle) {
    detail = apiTitle
    problemTitle = undefined
  } else if (typeof data === 'string' && data.trim()) {
    detail = data.trim().slice(0, 500)
    problemTitle = undefined
  } else {
    detail = error.message || fallback
    problemTitle = undefined
  }

  const code = status ?? body?.status ?? null
  return { statusCode: code, problemTitle, detail }
}

/**
 * App label, HTTP status, optional ProblemDetails title, and detail / validation body.
 */
export function getApiErrorParts(error: unknown, shortLabel?: string, fallback = 'Request failed'): ApiErrorParts {
  const title = shortLabel
  if (isAxiosError(error)) {
    const { statusCode, problemTitle, detail } = getAxiosErrorParts(error, fallback)
    return { title, statusCode, problemTitle, detail }
  }
  if (error instanceof Error) {
    return { title, statusCode: null, detail: error.message }
  }
  return { title, statusCode: null, detail: fallback }
}

/**
 * Single-line string for inline display (e.g. preview iframe).
 */
export function formatApiError(error: unknown, fallback = 'Request failed'): string {
  const { statusCode, problemTitle, detail } = getApiErrorParts(error, undefined, fallback)
  const parts: string[] = []
  if (statusCode != null) parts.push(`[${statusCode}]`)
  if (problemTitle) parts.push(problemTitle)
  parts.push(detail)
  return parts.join(' ')
}

export type ToastFn = (input: string | ApiErrorParts, type?: 'success' | 'error' | 'info') => void

/** Multiline error toast: app title, status, ProblemDetails title, then detail. */
export function toastApiError(toast: ToastFn, error: unknown, shortLabel?: string): void {
  toast(getApiErrorParts(error, shortLabel), 'error')
}
